import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import Label from "@components/form/Label";
import TextInput from "@components/form/TextInput";
import { useAppContext } from "@context/AppContext";
import { updateCurrentUserSubscriber } from "@services/user";
import FormDialog from "./FormDialog";

/** A primary user's edit of their subscriber's details, labelled as the Subscriber tab shows them. */
const EditSubscriberDialog: FunctionComponent<{
  onClose: () => void;
  onSaved: () => void;
}> = ({ onClose, onSaved }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    loggedInInfo: { userDetails },
  } = useAppContext();
  const subscriber = userDetails?.subscriber;
  const required = z.string().min(1, t("common.required.text") as string);
  const schema = z.object({
    name: required,
    abn: z.string().optional(),
    acn: z.string().optional(),
    street: z.string().optional(),
    town: z.string().optional(),
    region: required,
    postCode: required,
    country: required,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: subscriber?.name ?? "",
      abn: subscriber?.abn ?? "",
      acn: subscriber?.acn ?? "",
      street: subscriber?.address?.street ?? "",
      town: subscriber?.address?.town ?? "",
      region: subscriber?.address?.region ?? "",
      postCode: subscriber?.address?.postcode ?? "",
      country: subscriber?.address?.country ?? "",
    },
  });
  const { mutate, isLoading, isError } = useMutation(
    updateCurrentUserSubscriber,
    {
      onSuccess: () => {
        // The tab reads the subscriber from the session's user details.
        queryClient.invalidateQueries(["refreshToken"]);
        onSaved();
      },
    },
  );
  const fields: [keyof z.infer<typeof schema>, string][] = [
    ["name", t("subscriber.name")],
    ["abn", t("subscriber.abn")],
    ["acn", t("subscriber.acn")],
    ["street", t("profile.subscriber.street_field")],
    ["town", t("profile.subscriber.town_field")],
    ["region", t("profile.subscriber.region_field")],
    ["postCode", t("profile.subscriber.postcode_field")],
    ["country", t("profile.subscriber.country_field")],
  ];

  return (
    <FormDialog
      title={t("zenith.profile.edit_subscriber")}
      submitLabel={t("common.save")}
      onClose={onClose}
      onSubmit={handleSubmit(data => mutate(data))}
      busy={isLoading}
      error={isError && t("profile.subscriber.saved_failure")}
    >
      {fields.map(([field, label]) => (
        <div key={field}>
          <Label htmlFor={`subscriber-${field}`}>{label}</Label>
          <TextInput
            id={`subscriber-${field}`}
            {...register(field)}
            errorMessage={errors[field]?.message}
          />
        </div>
      ))}
    </FormDialog>
  );
};

export default EditSubscriberDialog;
